import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { id } from '@wonder-core-ng/interfaces/id.type';
import { BI_CORE_CONFIG } from '../config';
import {
  CreateDashboardBlockDTO,
  IDashboardBlock,
  SaveDashboardBlocksDTO,
} from './interfaces/dashboard-block.interface';
import { CreateDashboardDTO, IDashboard } from './interfaces/dashboard.interface';
import { IActionExecution } from '../event-store/interfaces/event.interface';

@Injectable()
export class DashboardApiService {
  private readonly httpClient = inject(HttpClient);
  private readonly config = inject(BI_CORE_CONFIG);

  readonly route = `${this.config.apiBaseUrl}/dashboard`;
  readonly blockRoute = `${this.config.apiBaseUrl}/dashboard-block`;
  private readonly actionExecutionRoute = `${this.config.apiBaseUrl}/action-executions`;

  loadDashboard(dashboardId: id, tenantId: id): Observable<IDashboard> {
    return this.httpClient.get<IDashboard>(`${this.route}/${dashboardId}`, {
      headers: this.headers(tenantId),
    });
  }

  loadDashboards(tenantId: id): Observable<IDashboard[]> {
    return this.httpClient.get<IDashboard[]>(this.route, {
      headers: this.headers(tenantId),
    });
  }

  createDashboard(dashboard: CreateDashboardDTO, tenantId: id): Observable<IDashboard> {
    return this.httpClient.post<IDashboard>(this.route, dashboard, {
      headers: this.headers(tenantId),
    });
  }

  updateDashboard(
    dashboardId: id,
    patch: Partial<CreateDashboardDTO>,
    tenantId: id
  ): Observable<IDashboard> {
    return this.httpClient.put<IDashboard>(`${this.route}/${dashboardId}`, patch, {
      headers: this.headers(tenantId),
    });
  }

  deleteDashboard(dashboardId: id, tenantId: id): Observable<void> {
    return this.httpClient.delete<void>(`${this.route}/${dashboardId}`, {
      headers: this.headers(tenantId),
    });
  }

  createBlock(dto: CreateDashboardBlockDTO, tenantId: id): Observable<IDashboardBlock> {
    return this.httpClient.post<IDashboardBlock>(this.blockRoute, dto, {
      headers: this.headers(tenantId),
    });
  }

  updateBlock(
    blockId: id,
    dto: Partial<CreateDashboardBlockDTO>,
    tenantId: id
  ): Observable<IDashboardBlock> {
    return this.httpClient.put<IDashboardBlock>(`${this.blockRoute}/${blockId}`, dto, {
      headers: this.headers(tenantId),
    });
  }

  deleteBlock(blockId: id, tenantId: id): Observable<void> {
    return this.httpClient.delete<void>(`${this.blockRoute}/${blockId}`, {
      headers: this.headers(tenantId),
    });
  }

  saveDashboardBlocks(
    dashboardId: id,
    dto: SaveDashboardBlocksDTO,
    tenantId: id
  ): Observable<IDashboardBlock[]> {
    return this.httpClient.put<IDashboardBlock[]>(`${this.route}/${dashboardId}/blocks`, dto, {
      headers: this.headers(tenantId),
    });
  }

  /**
   * Executions of every action belonging to any action list placed on this block.
   *
   * Scoped to the block rather than to one action list, because the block is the placement unit and
   * may hold several lists: each card narrows the result down to its own actions.
   *
   * The endpoint now always answers `{ list, total }` — `total` was added for the expand dialog's
   * own paginated fetch — even for this call, which asks for neither `limit` nor `offset` and wants
   * everything the block has. `.list` is unwrapped here so this method keeps promising a plain array,
   * same as before that endpoint learned to paginate.
   */
  loadBlockActionExecutions(blockId: id, tenantId: id): Observable<IActionExecution[]> {
    return this.httpClient
      .get<{ list: IActionExecution[]; total: number }>(
        `${this.blockRoute}/${blockId}/action-executions`,
        { headers: this.headers(tenantId) }
      )
      .pipe(map((res) => res.list));
  }

  /** Marks one `SEND_NOTIFICATION` execution as read, for the action list's read/unread dot. */
  markActionExecutionRead(executionId: number, tenantId: id): Observable<IActionExecution> {
    return this.httpClient.patch<IActionExecution>(
      `${this.actionExecutionRoute}/${executionId}/read`,
      {},
      { headers: this.headers(tenantId) }
    );
  }

  private headers(tenantId: id): { [key: string]: string } {
    return { 'x-tenant-id': tenantId.toString() };
  }
}
